import { Link } from "react-router-dom";
import { MapPin, Mail, Phone, Hammer } from "lucide-react";
import { DIVISIONS } from "@/data/catalog";
import { SEO_PAGES } from "@/data/seoPages";
import { useContact } from "@/context/ContactContext";

export const Footer = () => {
  const contact = useContact();
  return (
    <footer className="bg-[#0A1128] text-slate-300" data-testid="site-footer">
      <div className="h-1 copper-rule" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-1">
          <div className="flex items-center gap-2.5 mb-4">
            <span className="flex items-center justify-center w-9 h-9 rounded-md bg-gradient-to-br from-[#B87333] to-[#D97706] text-[#0A1128]">
              <Hammer size={20} strokeWidth={2.5} />
            </span>
            <span className="font-display font-extrabold text-white text-lg">
              WAAN<span className="text-[#D97706]">METAL</span>
            </span>
          </div>
          <p className="text-sm text-slate-400 leading-relaxed">
            Multi-disciplinary renovation, fit-out, interior design and metal fabrication contractor based in Singapore.
          </p>
          <p className="mt-4 text-xs font-mono-label uppercase tracking-wider text-[#D97706]">
            HDB-Registered Renovation Contractor
          </p>
        </div>

        <div>
          <h4 className="text-white font-semibold mb-4 text-sm">Services</h4>
          <ul className="space-y-2.5 text-sm">
            {DIVISIONS.map((d) => (
              <li key={d.slug}>
                <Link to={`/services/${d.slug}`} className="text-slate-400 hover:text-[#D97706] transition-colors">
                  {d.title}
                </Link>
              </li>
            ))}
            <li>
              <Link to="/interior-design" className="text-slate-400 hover:text-[#D97706] transition-colors">
                Interior Design
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="text-white font-semibold mb-4 text-sm">Company</h4>
          <ul className="space-y-2.5 text-sm">
            <li><Link to="/about" className="text-slate-400 hover:text-[#D97706]">About Us</Link></li>
            <li><Link to="/projects" className="text-slate-400 hover:text-[#D97706]">Projects</Link></li>
            <li><Link to="/price-guide" className="text-slate-400 hover:text-[#D97706]">Price Guide</Link></li>
            <li><Link to="/request-quotation" className="text-slate-400 hover:text-[#D97706]">Request a Quotation</Link></li>
            <li><Link to="/privacy" className="text-slate-400 hover:text-[#D97706]">Privacy Policy</Link></li>
            <li><Link to="/terms" className="text-slate-400 hover:text-[#D97706]">Terms of Service</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="text-white font-semibold mb-4 text-sm">Contact</h4>
          <ul className="space-y-3 text-sm text-slate-400">
            <li className="flex gap-2.5"><MapPin size={16} className="text-[#D97706] shrink-0 mt-0.5" /><span>{contact.address}</span></li>
            <li className="flex gap-2.5"><Mail size={16} className="text-[#D97706] shrink-0 mt-0.5" /><a href={`mailto:${contact.email}`} className="hover:text-[#D97706]">{contact.email}</a></li>
            {contact.phone ? (
              <li className="flex gap-2.5"><Phone size={16} className="text-[#D97706] shrink-0 mt-0.5" /><a href={`tel:+${(contact.phone || "").replace(/[^0-9]/g, "")}`} data-testid="footer-phone" className="hover:text-[#D97706]">{contact.phone}</a></li>
            ) : null}
            <li className="pt-1 text-xs text-slate-500">{contact.business_hours}</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <p className="eyebrow mb-3">Popular Searches</p>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {SEO_PAGES.map((p) => (
              <Link key={p.slug} to={`/${p.slug}`} data-testid={`footer-seo-${p.slug}`} className="text-xs text-slate-400 hover:text-[#D97706] transition-colors">{p.crumb} Singapore</Link>
            ))}
          </div>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row justify-between gap-2 text-xs text-slate-500">
          <p>© {new Date().getFullYear()} WAAN METAL PTE. LTD. All rights reserved.</p>
          <p>Regulated works are subject to applicable HDB, building, fire-safety and other approvals.</p>
        </div>
      </div>
    </footer>
  );
};
